import { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { Input, Modal, Select, TextArea } from "../../../../shared";
import {
  useGetCustomerEmailConfigQuery,
  useUpdateCustomerEmailConfigMutation,
} from "../../../../redux/services/customers/customersApi";

const YES_NO_OPTIONS = [
  { value: "true", label: "Yes" },
  { value: "false", label: "No" },
];

function buildForm(config = {}) {
  return {
    enabled: Boolean(config?.enabled),
    host: config?.host || "",
    port: config?.port ? String(config.port) : "",
    secure: config?.secure !== false,
    user: config?.auth?.user || "",
    pass: config?.auth?.pass || "",
    fromName: config?.fromName || "",
    fromEmail: config?.fromEmail || "",
    dkimPrivateKey: config?.dkimPrivateKey || "",
    dkimSelector: config?.dkimSelector || "",
    dkimDomain: config?.dkimDomain || "",
  };
}

function getEventValue(event) {
  if (event?.target) {
    return {
      id: event.target.id,
      value: event.target.value,
    };
  }

  return {
    id: event?.id,
    value: event?.value,
  };
}

const EmailConfigModal = ({ customerId, visible, setVisible }) => {
  const [form, setForm] = useState(buildForm());
  const { data: emailConfigResponse, isFetching } = useGetCustomerEmailConfigQuery(
    { id: customerId },
    { skip: !visible || !customerId }
  );
  const [updateCustomerEmailConfig, { isLoading: isSaving }] =
    useUpdateCustomerEmailConfigMutation();

  useEffect(() => {
    if (!visible) {
      return;
    }

    setForm(buildForm(emailConfigResponse?.data || {}));
  }, [emailConfigResponse, visible]);

  function updateForm(event) {
    const { id, value } = getEventValue(event);
    setForm((current) => ({
      ...current,
      [id]: value,
    }));
  }

  function updateBooleanField(id, value) {
    setForm((current) => ({
      ...current,
      [id]: value === "true",
    }));
  }

  async function handleSave() {
    if (!customerId) {
      return;
    }

    try {
      await updateCustomerEmailConfig({
        id: customerId,
        data: {
          enabled: form.enabled,
          host: form.host,
          port: form.port ? Number(form.port) : "",
          secure: form.secure,
          auth: {
            user: form.user,
            pass: form.pass,
          },
          fromName: form.fromName,
          fromEmail: form.fromEmail,
          dkimPrivateKey: form.dkimPrivateKey,
          dkimSelector: form.dkimSelector,
          dkimDomain: form.dkimDomain,
        },
      }).unwrap();

      toast("Email configuration updated", {
        type: "success",
        toastId: "customer-email-config-updated",
      });
      setVisible(false);
    } catch (error) {
      toast(error?.data?.message || "Unable to update email configuration", {
        type: "error",
        toastId: "customer-email-config-update-error",
      });
    }
  }

  const content = (
    <div className="space-y-4">
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <Select
          id="enabled"
          label="Custom Email Server"
          value={form.enabled ? "Yes" : "No"}
          options={YES_NO_OPTIONS}
          onChange={(event) => updateBooleanField("enabled", event.value)}
        />
        <Select
          id="secure"
          label="Use Secure SMTP"
          value={form.secure ? "Yes" : "No"}
          options={YES_NO_OPTIONS}
          onChange={(event) => updateBooleanField("secure", event.value)}
        />
      </div>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <Input
          id="host"
          label="SMTP Host"
          value={form.host}
          onChange={updateForm}
          placeholder="smtp.example.com"
        />
        <Input
          id="port"
          type="number"
          label="SMTP Port"
          value={form.port}
          onChange={updateForm}
          placeholder="587"
        />
      </div>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <Input
          id="user"
          label="SMTP Username"
          value={form.user}
          onChange={updateForm}
          placeholder="mailer@example.com"
        />
        <Input
          id="pass"
          type="password"
          label="SMTP Password"
          value={form.pass}
          onChange={updateForm}
          placeholder="********"
          info="Leave the masked value as-is to keep the current password. Re-enter it before changing SMTP host, port, secure mode, or username."
        />
      </div>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <Input
          id="fromName"
          label="From Name"
          value={form.fromName}
          onChange={updateForm}
          placeholder="Attune Logic"
        />
        <Input
          id="fromEmail"
          type="email"
          label="From Email"
          value={form.fromEmail}
          onChange={updateForm}
          placeholder="noreply@example.com"
        />
      </div>

      <TextArea
        id="dkimPrivateKey"
        label="DKIM Private Key"
        value={form.dkimPrivateKey}
        onChange={updateForm}
        rows={4}
        placeholder="Optional private key"
      />

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <Input
          id="dkimSelector"
          label="DKIM Selector"
          value={form.dkimSelector}
          onChange={updateForm}
          placeholder="mail"
        />
        <Input
          id="dkimDomain"
          label="DKIM Domain"
          value={form.dkimDomain}
          onChange={updateForm}
          placeholder="example.com"
        />
      </div>
    </div>
  );

  return (
    <Modal
      visible={visible}
      setVisible={setVisible}
      title="Customer Email Configuration"
      size="lg"
      loading={isSaving}
      confirm={handleSave}
      submitText="Save"
      footerPosition="right"
      content={isFetching ? <div className="py-8 text-center">Loading...</div> : content}
    />
  );
};

export default EmailConfigModal;
